import { useRef } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { Text, theme } from '@socrait/react';

export type TabConfig<K extends string> = {
  key: K;
  label: string;
  disabled?: boolean;
  telemetryLabel?: string;
};

type TabBarProps<K extends string> = {
  activeTab?: K;
  tabs: readonly TabConfig<K>[];
  onTabChange: (tab: K) => void;
};

export function TabBar<K extends string>({ activeTab, tabs, onTabChange }: TabBarProps<K>) {
  const tabRefs = useRef<(HTMLElement | null)[]>([]);

  const focusableIndices = tabs.map((t, i) => (t.disabled ? -1 : i)).filter((i) => i !== -1);

  const handleKeyDown = (e: KeyboardEvent, index: number) => {
    if (focusableIndices.length === 0) return;
    const currentPos = focusableIndices.indexOf(index);
    if (currentPos === -1) return;

    let nextIndex: number | null = null;

    switch (e.key) {
      case 'ArrowRight':
        nextIndex = focusableIndices[(currentPos + 1) % focusableIndices.length]!;
        break;
      case 'ArrowLeft':
        nextIndex = focusableIndices[(currentPos - 1 + focusableIndices.length) % focusableIndices.length]!;
        break;
      case 'Home':
        nextIndex = focusableIndices[0]!;
        break;
      case 'End':
        nextIndex = focusableIndices[focusableIndices.length - 1]!;
        break;
    }

    if (nextIndex !== null) {
      e.preventDefault();
      onTabChange(tabs[nextIndex]!.key);
      tabRefs.current[nextIndex]?.focus?.();
    }
  };

  return (
    <View style={styles.container} accessibilityRole="tablist">
      {tabs.map(({ key, label, disabled, telemetryLabel }, index) => (
        <Pressable
          key={key}
          ref={(el) => {
            tabRefs.current[index] = el as unknown as HTMLElement;
          }}
          onPress={() => {
            if (!disabled) onTabChange(key);
          }}
          disabled={disabled}
          style={styles.tab}
          accessibilityRole="tab"
          accessibilityState={{ selected: activeTab === key, disabled: !!disabled }}
          {...(telemetryLabel ? { 'telemetry-label': telemetryLabel } : {})}
          {...({ onKeyDown: (e: KeyboardEvent) => handleKeyDown(e, index) } as object)}>
          <Text style={[styles.label, disabled && styles.labelDisabled]}>{label}</Text>
          {activeTab === key && <View style={styles.underline} />}
        </Pressable>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xlarge,
  },
  tab: {
    alignItems: 'center',
  },
  label: {
    fontSize: theme.fontSizes.medium,
    fontWeight: theme.fontWeights.bold,
  },
  labelDisabled: {
    opacity: 0.4,
  },
  underline: {
    position: 'absolute',
    bottom: -theme.spacing.small,
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: theme.colors.navy,
  },
});
